import { useEffect, useRef } from 'react';
import { message } from 'antd';
import { CopyOutlined, LinkOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';

interface TileContextMenuProps {
  visible: boolean;
  position: { x: number; y: number };
  tileUrl: string | null;
  tileCoords: { z: number; x: number; y: number } | null;
  lngLat: { lng: number; lat: number } | null;
  onClose: () => void;
  onViewNowcasting?: () => void;
  showNowcastingOption?: boolean;
}

export const TileContextMenu = ({
  visible,
  position,
  tileUrl,
  tileCoords,
  lngLat,
  onClose,
  onViewNowcasting,
  showNowcastingOption,
}: TileContextMenuProps) => {
  const { theme } = useTheme();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onClose]);

  if (!visible || !tileUrl) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tileUrl);
      message.success('Tile URL copied to clipboard');
    } catch {
      message.error('Failed to copy URL');
    }
    onClose();
  };

  const handleOpenInNewTab = () => {
    window.open(tileUrl, '_blank');
    onClose();
  };

  const isDark = theme === 'dark';

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 10000,
        backgroundColor: isDark ? '#1f1f1f' : '#fff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,.2)' : 'rgba(0,0,0,.15)'}`,
        borderRadius: 6,
        boxShadow: isDark
          ? '0 6px 16px rgba(0,0,0,.5)'
          : '0 6px 16px rgba(0,0,0,.12)',
        padding: '8px 0',
        minWidth: 280,
        maxWidth: 420,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 12,
      }}
    >
      {/* Header with coordinates */}
      <div style={{
        padding: '4px 12px 8px',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.06)'}`,
        color: isDark ? 'rgba(255,255,255,.45)' : 'rgba(0,0,0,.45)',
        fontSize: 11,
      }}>
        {lngLat && (
          <div>Location: {lngLat.lat.toFixed(5)}, {lngLat.lng.toFixed(5)}</div>
        )}
        {tileCoords && (
          <div>Tile: z={tileCoords.z} x={tileCoords.x} y={tileCoords.y}</div>
        )}
      </div>

      {/* URL preview */}
      <div style={{
        padding: '8px 12px',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.06)'}`,
        color: isDark ? 'rgba(255,255,255,.65)' : 'rgba(0,0,0,.65)',
        fontSize: 10,
        fontFamily: 'monospace',
        wordBreak: 'break-all',
        maxHeight: 60,
        overflow: 'auto',
      }}>
        {tileUrl}
      </div>

      {/* Actions */}
      <div
        onClick={handleCopy}
        style={{
          padding: '6px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: isDark ? '#fff' : '#333',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.04)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <CopyOutlined /> Copy tile URL
      </div>

      <div
        onClick={handleOpenInNewTab}
        style={{
          padding: '6px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: isDark ? '#fff' : '#333',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.04)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <LinkOutlined /> Open tile in new tab
      </div>

      {showNowcastingOption && onViewNowcasting && (
        <>
          <div style={{
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.06)'}`,
            margin: '4px 0',
          }} />
          <div
            onClick={() => {
              onViewNowcasting();
              onClose();
            }}
            style={{
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: isDark ? '#fff' : '#333',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <PlayCircleOutlined /> View nowcasting tiles
          </div>
        </>
      )}
    </div>
  );
};
